import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './Aide.css';

/**
 * LE SCHÉMA D'UN PROCESSUS — commun à tous les modules (étape A2)
 * ================================================================
 * Règles : doc/specs_techniques/charte_graphique.md § Schémas de processus (S1 → S13)
 * Maquette : doc/maquettes/aide_contextuelle_maquette.html (fenêtre « Comprendre le processus métier »)
 *
 * Les étapes sont du HTML (grille de 6 colonnes — davantage si une rangée est plus longue, la fenêtre s'élargit alors) ; les liaisons sont un SVG posé par-dessus, calculé d'après la
 * position MESURÉE des étapes (au montage, et à chaque changement de taille du schéma). Aucune bibliothèque (S13).
 *   S1/S2 droites à coins arrondis, dernier segment droit, pointe dans son axe ;
 *   S3 retours en pointillé, un couloir chacun au-dessus de la ligne ; S4 les 10 derniers px pleins sous la pointe ;
 *   S5 boucle sur la même étape : sous l'étape ; S6 passage à la ligne suivante libellé ; S10 survol.
 */

const R = 8;            // rayon des coins (S1)
const COULOIR = 26;     // écart entre deux couloirs de retour (S3)
const PLEIN = 10;       // longueur pleine sous la pointe d'un pointillé (S4)

/** Une ligne brisée à coins arrondis → attribut `d` d'un chemin SVG. */
function ortho(pts) {
    let d = `M${pts[0][0]} ${pts[0][1]}`;
    for (let i = 1; i < pts.length - 1; i += 1) {
        const [p0, p1, p2] = [pts[i - 1], pts[i], pts[i + 1]];
        const l1 = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
        const l2 = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
        const r = Math.min(R, l1 / 2, l2 / 2);
        const a = [p1[0] - ((p1[0] - p0[0]) / l1) * r, p1[1] - ((p1[1] - p0[1]) / l1) * r];
        const b = [p1[0] + ((p2[0] - p1[0]) / l2) * r, p1[1] + ((p2[1] - p1[1]) / l2) * r];
        d += ` L${a[0]} ${a[1]} Q${p1[0]} ${p1[1]} ${b[0]} ${b[1]}`;
    }
    const n = pts[pts.length - 1];
    return `${d} L${n[0]} ${n[1]}`;
}

/** Le nombre de colonnes du schéma : sa plus longue rangée, 6 au moins (Stagiaires, E173 : 7 → deux rangées). */
export const colonnesDe = (processus) => Math.max(6, ...processus.lignes.map((l) => l.length));

/** Les liaisons du processus, d'après la géométrie mesurée. */
function liaisons(processus, geo) {
    const res = [];
    const bx = (k) => geo[k];
    processus.lignes.forEach((ligne) => {
        for (let i = 0; i < ligne.length - 1; i += 1) {
            const [a, b] = [bx(ligne[i]), bx(ligne[i + 1])];
            if (!a || !b || processus.etapes[ligne[i + 1]].sortie) continue;
            res.push({ cle: `s${ligne[i]}`, de: ligne[i], vers: ligne[i + 1], pts: [[a.r, a.cy], [b.l - 1, b.cy]] });
        }
    });
    // S6 : le passage d'une ligne à la suivante (aucun si `passage: false` : la ligne suivante est une autre issue).
    for (let li = 0; processus.passage !== false && li < processus.lignes.length - 1; li += 1) {
        const fin = processus.lignes[li][processus.lignes[li].length - 1];
        const deb = processus.lignes[li + 1][0];
        const [a, b] = [bx(fin), bx(deb)];
        if (!a || !b) continue;
        const y = (a.b + b.t) / 2;
        res.push({ cle: `p${li}`, de: fin, vers: deb, pts: [[a.cx, a.b], [a.cx, y], [b.cx, y], [b.cx, b.t - 1]],
                   libelle: processus.passage, lx: (a.cx + b.cx) / 2, ly: y - 6 });
    }
    // Une autre issue (`branches`) : flèche PLEINE vers une étape de la ligne suivante, même tracé que le passage.
    (processus.branches || []).forEach((br, i) => {
        const [a, b] = [bx(br.de), bx(br.vers)];
        if (!a || !b) return;
        const y = (a.b + b.t) / 2;
        res.push({ cle: `b${i}`, de: br.de, vers: br.vers, pts: [[a.cx, a.b], [a.cx, y], [b.cx, y], [b.cx, b.t - 1]],
                   libelle: br.libelle, lx: (a.cx + b.cx) / 2, ly: y - 6 });
    });
    (processus.retours || []).forEach((rt, i) => {
        const [a, b] = [bx(rt.de), bx(rt.vers)];
        if (!a || !b) return;
        const x = (bt, ou) => (ou === 'gauche' ? bt.l + 22 : ou === 'droite' ? bt.r - 22 : bt.cx);
        if (rt.bas) {
            const yb = a.b + 22 * rt.couloir;
            res.push({ cle: `r${i}`, de: rt.de, vers: rt.vers, retour: true, echec: rt.echec,
                       pts: [[a.cx + 28, a.b], [a.cx + 28, yb], [a.cx - 28, yb], [a.cx - 28, a.b + 1]],
                       libelle: rt.libelle, lx: a.cx, ly: yb + 15 });
        } else {
            const [x1, x2] = [x(a, rt.depart), x(b, rt.arrivee)];
            const yh = Math.min(a.t, b.t) - COULOIR * rt.couloir;
            res.push({ cle: `r${i}`, de: rt.de, vers: rt.vers, retour: true, echec: rt.echec,
                       pts: [[x1, a.t], [x1, yh], [x2, yh], [x2, b.t - 1]],
                       libelle: rt.libelle, lx: (x1 + x2) / 2, ly: yh - 5 });
        }
    });
    return res;
}

/** Un trait : plein, ou pointillé dont les 10 derniers px sont pleins sous la pointe (S4). */
function Trait({ l }) {
    const couleur = l.echec ? '#dc2626' : '#64748b';
    const pointe = `url(#${l.echec ? 'aide-fleche-ko' : 'aide-fleche'})`;
    if (!l.retour) return <path d={ortho(l.pts)} fill="none" stroke={couleur} strokeWidth="1.7" markerEnd={pointe} />;
    const n = l.pts[l.pts.length - 1];
    const m = l.pts[l.pts.length - 2];
    const L = Math.hypot(n[0] - m[0], n[1] - m[1]);
    const k = Math.min(PLEIN, L / 2);
    const c = [n[0] - ((n[0] - m[0]) / L) * k, n[1] - ((n[1] - m[1]) / L) * k];
    return (
        <>
            <path d={ortho([...l.pts.slice(0, -1), c])} fill="none" stroke={couleur} strokeWidth="1.7" strokeDasharray="5 4" />
            <path d={`M${c[0]} ${c[1]} L${n[0]} ${n[1]}`} fill="none" stroke={couleur} strokeWidth="1.7" markerEnd={pointe} />
        </>
    );
}

function SchemaProcessus({ processus, acteurs, ecran, choisie, onChoisir }) {
    const conteneur = useRef(null);
    const boites = useRef({});
    const [geo, setGeo] = useState(null);
    const [survol, setSurvol] = useState(null);

    // La géométrie se MESURE (jamais supposée) ; on ne la repose que si elle a changé.
    const mesurer = useCallback(() => {
        const c = conteneur.current;
        if (!c) return;
        const o = c.getBoundingClientRect();
        const g = {};
        Object.entries(boites.current).forEach(([k, el]) => {
            if (!el) return;
            const r = el.getBoundingClientRect();
            g[k] = { l: r.left - o.left, r: r.right - o.left, t: r.top - o.top, b: r.bottom - o.top,
                     cx: (r.left + r.right) / 2 - o.left, cy: (r.top + r.bottom) / 2 - o.top };
        });
        setGeo((avant) => (JSON.stringify(avant) === JSON.stringify(g) ? avant : g));
    }, []);
    useLayoutEffect(() => { mesurer(); }, [mesurer, processus]);
    useEffect(() => {
        if (!conteneur.current || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => mesurer());
        ro.observe(conteneur.current);
        return () => ro.disconnect();
    }, [mesurer]);

    const traits = useMemo(() => (geo ? liaisons(processus, geo) : []), [processus, geo]);

    // S10 : l'étape survolée (sinon celle choisie au clic) — ses liaisons et ses voisines ressortent.
    const focus = survol != null ? survol : choisie;
    const lies = useMemo(() => {
        if (focus == null) return null;
        const etapes = new Set([String(focus)]);
        const traitsLies = new Set();
        traits.forEach((t) => {
            if (t.de === String(focus) || t.vers === String(focus)) { traitsLies.add(t.cle); etapes.add(t.de); etapes.add(t.vers); }
        });
        return { etapes, traits: traitsLies };
    }, [focus, traits]);

    return (
        <div className={'aide-schema' + (lies ? ' focus' : '')} ref={conteneur}>
            <svg className="aide-schema-svg" aria-hidden="true">
                <defs>
                    <marker id="aide-fleche" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M0,0 L10,5 L0,10 z" fill="#64748b" />
                    </marker>
                    <marker id="aide-fleche-ko" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M0,0 L10,5 L0,10 z" fill="#dc2626" />
                    </marker>
                </defs>
                {traits.map((t) => (
                    <g key={t.cle} data-de={t.de} data-vers={t.vers} className={'aide-trait' + (lies && lies.traits.has(t.cle) ? ' lie' : '')}>
                        <Trait l={t} />
                        {t.libelle && <text className={'aide-trait-lbl' + (t.echec ? ' ko' : '')} x={t.lx} y={t.ly} textAnchor="middle">{t.libelle}</text>}
                    </g>
                ))}
            </svg>
            {processus.lignes.map((ligne) => (
                <div className="aide-s-ligne" key={ligne.join('-')} style={{ gridTemplateColumns: `repeat(${colonnesDe(processus)}, minmax(0, 1fr))` }}>
                    {ligne.map((k) => {
                        const e = processus.etapes[k];
                        const ici = e.ecrans.includes(ecran);
                        const cls = ['aide-etape', e.prealable && 'prealable', e.facultative && 'facultative', e.sortie && 'sortie',
                            ici && 'ici', String(choisie) === k && 'choisie', lies && lies.etapes.has(k) && 'lie'].filter(Boolean).join(' ');
                        return (
                            <div key={k} ref={(el) => { boites.current[k] = el; }} className={cls} data-etape={k}
                                 role="button" tabIndex={0} title={e.libelle}
                                 onClick={() => onChoisir(k)} onKeyDown={(ev) => { if (ev.key === 'Enter') onChoisir(k); }}
                                 onMouseEnter={() => setSurvol(k)} onMouseLeave={() => setSurvol(null)}>
                                <div className="aide-e-hd">
                                    <span className="aide-e-num">
                                        {e.sortie ? <i className="fa-solid fa-right-from-bracket" /> : e.prealable ? <i className="fa-solid fa-gear" /> : e.icone ? <i className={'fa-solid ' + e.icone} /> : k}
                                    </span>
                                    <span className="aide-e-lb">{e.libelle}</span>
                                    {ici && <i className="fa-solid fa-location-dot aide-e-ici" title="Se traite sur cet écran" />}
                                </div>
                                <div className="aide-e-ac">
                                    {e.acteurs.map((a) => <span key={a} style={{ background: acteurs[a].couleur }}>{acteurs[a].libelle}</span>)}
                                </div>
                                <div className="aide-e-bd">
                                    {e.relance && <span><i className="fa-solid fa-rotate-right" /> relance</span>}
                                    {e.embranchement && <span><i className="fa-solid fa-code-branch" /> {e.embranchement} issues</span>}
                                    {e.facultative && <span><i className="fa-solid fa-circle-half-stroke" /> {typeof e.facultative === 'string' ? e.facultative : 'selon la formation'}</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}

export default SchemaProcessus;
